export type GoogleMapsLatLngLiteral = {
  lat: number;
  lng: number;
};

export type GoogleMapsLatLng = {
  lat: () => number;
  lng: () => number;
};

export type GoogleMapsMapClickEvent = {
  latLng?: GoogleMapsLatLng | null;
};

export type GoogleMapsMap = {
  addListener: (
    eventName: "click",
    handler: (event: GoogleMapsMapClickEvent) => void
  ) => void;
  setCenter: (center: GoogleMapsLatLngLiteral) => void;
};

export type GoogleMapsMarkerMouseEvent = {
  latLng?: GoogleMapsLatLng | null;
};

export type GoogleMapsAdvancedMarker = HTMLElement & {
  addListener: (
    eventName: "drag" | "dragend",
    handler: (event: GoogleMapsMarkerMouseEvent) => void
  ) => void;
  map: GoogleMapsMap | null;
  position?: GoogleMapsLatLng | GoogleMapsLatLngLiteral | null;
};

export type GoogleMapsCircle = {
  setCenter: (center: GoogleMapsLatLngLiteral) => void;
  setMap: (map: GoogleMapsMap | null) => void;
  setRadius: (radius: number) => void;
};

type LoadedGoogleMaps = {
  maps: {
    Map: new (
      element: HTMLElement,
      options?: Record<string, unknown>
    ) => GoogleMapsMap;
    Circle: new (options?: Record<string, unknown>) => GoogleMapsCircle;
    marker: {
      AdvancedMarkerElement: new (
        options?: Record<string, unknown>
      ) => GoogleMapsAdvancedMarker;
      PinElement: new (options?: Record<string, unknown>) => HTMLElement;
    };
  };
};

type GoogleMapsWindow = Window & {
  google?: LoadedGoogleMaps;
  __stonecutGoogleMapsInit?: () => void;
  gm_authFailure?: () => void;
};

const GOOGLE_MAPS_SCRIPT_ID = "google-maps-javascript-api";
const GOOGLE_MAPS_CALLBACK_NAME = "__stonecutGoogleMapsInit";
const LOAD_TIMEOUT_MS = 10000;

let googleMapsPromise: Promise<LoadedGoogleMaps> | null = null;

function getGoogleMaps(windowObject: GoogleMapsWindow) {
  const google = windowObject.google;
  if (
    !google?.maps?.Map ||
    !google.maps.Circle ||
    !google.maps.marker?.AdvancedMarkerElement ||
    !google.maps.marker?.PinElement
  ) {
    throw new Error("Google Maps loaded but failed to initialize.");
  }
  return google;
}

export function buildGoogleMapsScriptUrl(apiKey: string) {
  return (
    `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
    `&v=weekly&loading=async&libraries=marker&callback=${GOOGLE_MAPS_CALLBACK_NAME}`
  );
}

export async function loadGoogleMapsApi(apiKey: string) {
  if (typeof window === "undefined") {
    throw new Error("Google Maps can only load in the browser.");
  }

  const trimmedKey = apiKey.trim();
  if (!trimmedKey) {
    throw new Error(
      "Map search is currently unavailable. You can still enter coordinates manually."
    );
  }

  const windowObject = window as GoogleMapsWindow;

  if (windowObject.google?.maps?.Map) {
    return getGoogleMaps(windowObject);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(
      GOOGLE_MAPS_SCRIPT_ID
    ) as HTMLScriptElement | null;
    let scriptElement = existingScript;
    let settled = false;

    function handleLoad() {
      if (settled) {
        return;
      }

      try {
        const google = getGoogleMaps(windowObject);
        settled = true;
        cleanup();
        resolve(google);
      } catch (error) {
        fail(
          error instanceof Error
            ? error.message
            : "Google Maps loaded but failed to initialize."
        );
      }
    }

    function handleError() {
      fail(
        "Google Maps could not be loaded. You can still enter coordinates manually."
      );
    }

    const previousAuthFailure = windowObject.gm_authFailure;
    const previousInitCallback = windowObject[GOOGLE_MAPS_CALLBACK_NAME];
    const timeoutId = window.setTimeout(() => {
      fail(
        "Google Maps took too long to load. You can still enter coordinates manually."
      );
    }, LOAD_TIMEOUT_MS);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      windowObject.gm_authFailure = previousAuthFailure;
      windowObject[GOOGLE_MAPS_CALLBACK_NAME] = previousInitCallback;
      if (!scriptElement) {
        return;
      }
      scriptElement.removeEventListener("error", handleError);
    };

    const fail = (message: string) => {
      if (settled) {
        return;
      }
      settled = true;
      googleMapsPromise = null;
      cleanup();
      reject(new Error(message));
    };

    windowObject.gm_authFailure = () => {
      previousAuthFailure?.();
      fail(
        "Map search is currently unavailable. You can still enter coordinates manually."
      );
    };

    windowObject[GOOGLE_MAPS_CALLBACK_NAME] = () => {
      previousInitCallback?.();
      handleLoad();
    };

    if (existingScript) {
      if (windowObject.google?.maps?.Map) {
        handleLoad();
        return;
      }

      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = buildGoogleMapsScriptUrl(trimmedKey);
    scriptElement = script;
    script.addEventListener("error", handleError, { once: true });
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}
